import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigType, getSystemConfig, SystemConfigItem, updateSystemConfig } from 'services/systemConfigServices';
import PaginationData from 'types/models/PaginationData';
import { Table, TableHead, TableBody, TableRow, TableCell, TableEmpty, TablePagination } from 'components/table';
import useDebounce from 'hooks/useDebounce';
import 'styles/pages/SystemConfigPage.scss';

export default function SystemConfigPage(): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<ConfigType | 'all'>('all');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [paginationSystemConfig, setPaginationSystemConfig] = React.useState<PaginationData<SystemConfigItem> | null>(
    null,
  );
  const [selectedConfig, setSelectedConfig] = React.useState<SystemConfigItem | null>(null);
  const [editValue, setEditValue] = React.useState('');
  const [editDesc, setEditDesc] = React.useState('');

  const openEditModal = (config: SystemConfigItem): void => {
    setSelectedConfig(config);
    setEditValue(config.value);
    setEditDesc(config.desc);
  };

  const closeModal = (): void => {
    setSelectedConfig(null);
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedConfig) return;

    try {
      await updateSystemConfig({
        ...selectedConfig,
        value: editValue,
        desc: editDesc,
      });

      await loadData(currentPage, search);
      closeModal();
    } catch (error) {
      alert('Hubo un error al guardar la configuración');
      console.log('Hubo un error al guardar la configuración', error);
    }
  };

  const toggleBoolean = (): void => {
    setEditValue((prev) => (prev === '1' ? '0' : '1'));
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = React.useCallback(async (page?: number, searchTerm?: string) => {
    try {
      const paginationData = await getSystemConfig(page, searchTerm);
      if (paginationData) setPaginationSystemConfig(paginationData);
    } catch (error) {
      console.log('Error fetching system config:', error);
    }
  }, []);

  useDebounce(() => {
    setCurrentPage(1);
    loadData(1, search.trim() || undefined);
  }, [search]);

  const filteredConfigs = React.useMemo(() => {
    if (!paginationSystemConfig?.data) return [];
    return paginationSystemConfig?.data.filter((config) => {
      const query = search.toLowerCase();

      const matchesSearch =
        config.key.toLowerCase().includes(query) ||
        config.desc.toLowerCase().includes(query) ||
        config.value.toLowerCase().includes(query);

      const matchesFilter = activeFilter === 'all' || config.type === activeFilter;

      return matchesSearch && matchesFilter;
    });
  }, [paginationSystemConfig?.data, search, activeFilter]);

  const renderValue = (config: SystemConfigItem): JSX.Element => {
    if (config.type === 'bool') {
      const enabled = config.value === '1';

      return (
        <span className={`badge ${enabled ? 'badge-green' : 'badge-gray'}`}>
          {enabled ? '✓ Activado' : '— Desactivado'}
        </span>
      );
    }

    return <span className="td-val">{config.value}</span>;
  };

  const renderType = (type: ConfigType): JSX.Element => {
    const labels: Record<ConfigType, string> = {
      bool: 'Booleano',
      int: 'Entero',
      str: 'Texto',
    };

    return <span className={`type-pill type-${type}`}>{labels[type]}</span>;
  };

  return (
    <div className="system-config-page">
      <div className="system-config-page__header">
        <div>
          <h1 className="page-title">{t('system-config.title')}</h1>
          <p className="page-sub">{t('system-config.description')}</p>
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="6.5" cy="6.5" r="4" />
            <path d="M11 11l2.5 2.5" />
          </svg>

          <input
            type="text"
            placeholder="Buscar parámetro…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button
          className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          {t('system-config.filter-all')}
        </button>

        <button
          className={`filter-btn ${activeFilter === 'bool' ? 'active' : ''}`}
          onClick={() => setActiveFilter('bool')}
        >
          {t('system-config.filter-bool')}
        </button>

        <button
          className={`filter-btn ${activeFilter === 'int' ? 'active' : ''}`}
          onClick={() => setActiveFilter('int')}
        >
          {t('system-config.filter-int')}
        </button>

        <button
          className={`filter-btn ${activeFilter === 'str' ? 'active' : ''}`}
          onClick={() => setActiveFilter('str')}
        >
          {t('system-config.filter-str')}
        </button>
      </div>

      <div className="card">
        <div className="table-responsive">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell head className="id-column">
                  #
                </TableCell>
                <TableCell head>Clave</TableCell>
                <TableCell head>Valor</TableCell>
                <TableCell head>Tipo</TableCell>
                <TableCell head>Descripción</TableCell>
                <TableCell head className="actions-column">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredConfigs.length > 0 ? (
                filteredConfigs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell className="table-id">{config.id}</TableCell>
                    <TableCell>
                      <span className="td-key">{config.key}</span>
                    </TableCell>
                    <TableCell>{renderValue(config)}</TableCell>
                    <TableCell>{renderType(config.type)}</TableCell>
                    <TableCell className="table-desc">{config.desc}</TableCell>
                    <TableCell className="actions-column">
                      <div onClick={(event) => event.stopPropagation()}>
                        <button className="icon-btn" onClick={() => openEditModal(config)}>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          >
                            <path d="M11 2l3 3-9 9H2v-3l9-9z" />
                          </svg>
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableEmpty colSpan={6} message={t('generic-list-empty-state')} />
              )}
            </TableBody>
          </Table>
        </div>

        {selectedConfig && (
          <div className="modal-backdrop open" onClick={closeModal}>
            <div className="modal" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">
                    {t('edit')} · {selectedConfig.key}
                  </div>

                  <div className="modal-sub">system_config · id {selectedConfig.id}</div>
                </div>

                <button className="modal-close" onClick={closeModal}>
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="form-field">
                  <label className="form-label">{t('system-config.key')}</label>
                  <input className="form-input disabled" value={selectedConfig.key} disabled />
                  <span className="form-hint">{t('system-config.key-unmutable')}</span>
                </div>

                <div className="form-field">
                  <label className="form-label">{t('system-config.value')}</label>

                  {selectedConfig.type === 'bool' ? (
                    <div className="toggle-wrap" onClick={toggleBoolean}>
                      <div className={`toggle ${editValue === '1' ? 'on' : ''}`} />

                      <div>
                        <div className="toggle-label">
                          {editValue === '1' ? t('system-config.active') : t('system-config.disabled')}
                        </div>

                        <div className="toggle-sub">
                          {t('system-config.value')}: {editValue}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <input
                        className="form-input"
                        type={selectedConfig.type === 'int' ? 'number' : 'text'}
                        value={editValue}
                        onChange={(event) => setEditValue(event.target.value)}
                      />

                      <span className="form-hint">
                        {t('type')}:{' '}
                        {selectedConfig.type === 'int' ? t('system-config.int-value') : t('system-config.free-text')}
                      </span>
                    </>
                  )}
                </div>

                <div className="form-field">
                  <label className="form-label">{t('description')}</label>
                  <input
                    disabled
                    className="form-input disabled"
                    value={editDesc}
                    onChange={(event) => setEditDesc(event.target.value)}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  {t('cancel')}
                </button>

                <button className="btn btn-primary" onClick={handleSave}>
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        )}

        <TablePagination
          currentPage={currentPage}
          total={paginationSystemConfig?.total ?? 0}
          perPage={paginationSystemConfig?.perPage ?? 0}
          totalItems={paginationSystemConfig?.total ?? 0}
          onPageChange={loadData}
        />
      </div>
    </div>
  );
}
